import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { cn } from "~/foundation/utils/cn";
import {
  GroupHeading,
  NAV_ROW_ATTR,
  NavResults,
  NavRow,
  NavSearchInput,
  useNavRowKeys,
} from "~/shell/NavPrimitives";
import {
  activeDestination,
  flatten,
  type NavLayerSection,
  navLayerSections,
  resolveNavSection,
  searchNav,
} from "~/shell/navCatalog";
import { useShellStore } from "~/shell/store";

export interface NavLauncherProps {
  /**
   * "popover" — the Console Bar product picker; "sidebar" — the sections layer inside the
   * left nav (reached with "All sections").
   */
  variant: "popover" | "sidebar";
  /** After a row navigates (close the popover / leave the sections layer). */
  onNavigate?: () => void;
  /** Escape with nothing left to unwind (empty search, top layer). */
  onDismiss?: () => void;
  autoFocus?: boolean;
  /** Sidebar variant: the row that returns to the workspace nav. */
  backToSection?: { label: string; onBack: () => void };
}

/**
 * The layered launcher (2026-09-09). Layer 0 lists pinned favourites and the sections;
 * choosing a section drills into layer 1 — every destination of that section, grouped,
 * nothing capped — with "‹ All sections" on top. Typing searches every layer at once.
 * Arrow keys rove; Escape/Backspace unwind one layer at a time.
 */
export function NavLauncher({
  variant,
  onNavigate,
  onDismiss,
  autoFocus,
  backToSection,
}: NavLauncherProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: workspace } = useCurrentWorkspace();
  const pinnedIds = useShellStore((state) => state.pinnedNavIds);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [layerId, setLayerId] = useState<string | null>(null);
  const onRowKeys = useNavRowKeys(containerRef);

  const abilities = workspace?.abilities ?? {};
  const sections = useMemo(() => navLayerSections(abilities), [abilities]);
  const catalog = useMemo(() => flatten(sections), [sections]);
  const active = activeDestination(catalog, pathname);
  const current = resolveNavSection(sections, pathname);
  const layer = layerId ? sections.find((section) => section.id === layerId) : undefined;
  const hits = useMemo(() => searchNav(sections, query, t), [sections, query, t]);
  const pinned = useMemo(
    () =>
      pinnedIds
        .map((id) => catalog.find((destination) => destination.id === id))
        .filter((destination): destination is NonNullable<typeof destination> => !!destination),
    [pinnedIds, catalog],
  );

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const goBack = useCallback(() => {
    setLayerId(null);
    inputRef.current?.focus();
  }, []);

  const openLayer = useCallback((id: string) => {
    setLayerId(id);
    setQuery("");
    // Keep focus inside the panel so arrow keys keep working after the swap.
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const finish = useCallback(() => {
    setQuery("");
    onNavigate?.();
  }, [onNavigate]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      if (query) {
        event.preventDefault();
        event.stopPropagation();
        setQuery("");
        return;
      }
      if (layer) {
        event.preventDefault();
        event.stopPropagation();
        goBack();
        return;
      }
      // Nothing to unwind: let the host (a Radix popover, the sidebar) close too.
      onDismiss?.();
      return;
    }
    if (event.key === "Backspace" && event.target === inputRef.current && !query && layer) {
      event.preventDefault();
      goBack();
      return;
    }
    if (event.key === "Enter" && event.target === inputRef.current) {
      const first = containerRef.current?.querySelector<HTMLElement>(`[${NAV_ROW_ATTR}]`);
      if (first) {
        event.preventDefault();
        first.click();
      }
      return;
    }
    onRowKeys(event);
  };

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKeyDown}
      role="navigation"
      aria-label={t("Sections")}
      className={cn(
        "flex min-h-0 flex-col",
        variant === "popover" ? "max-h-[min(70vh,560px)] w-full" : "flex-1",
      )}
    >
      <div className="flex shrink-0 flex-col gap-1.5 px-2.5 pt-2 pb-1.5">
        {variant === "sidebar" && backToSection && !layer && !query ? (
          <BackRow label={backToSection.label} onClick={backToSection.onBack} />
        ) : null}
        {layer && !query ? <BackRow label={t("All sections")} onClick={goBack} /> : null}
        <NavSearchInput
          value={query}
          onChange={setQuery}
          inputRef={inputRef}
          placeholder={
            layer
              ? t("Find an area in {{section}}…", { section: t(layer.title) })
              : t("Find an area…")
          }
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-1.5 scrollbar-thin">
        {query ? (
          <NavResults hits={hits} query={query} activeId={active?.id} onNavigate={finish} />
        ) : layer ? (
          <SectionLayer
            section={layer}
            activeId={active?.id}
            onNavigate={finish}
            onOpenSection={() => {
              const first = layer.groups[0]?.items[0];
              if (first) {
                void navigate({ to: first.to as never });
                finish();
              }
            }}
          />
        ) : (
          <>
            <GroupHeading>{t("Pinned")}</GroupHeading>
            {pinned.length > 0 ? (
              <div className="flex flex-col gap-0.5 px-1">
                {pinned.map((destination) => (
                  <NavRow
                    key={destination.id}
                    destination={destination}
                    active={destination.id === active?.id}
                    onNavigate={finish}
                  />
                ))}
              </div>
            ) : (
              <p className="px-3 pb-2 text-small text-text-tertiary">
                {t("Star any area to keep it here and in the top bar.")}
              </p>
            )}

            <GroupHeading>{t("Sections")}</GroupHeading>
            <div className="flex flex-col gap-0.5 px-1">
              {sections.map((section) => {
                const Icon = section.icon;
                const isCurrent = section.id === current?.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => openLayer(section.id)}
                    data-nav-row=""
                    aria-current={isCurrent ? "true" : undefined}
                    className={cn(
                      "flex min-h-11 w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-start outline-none",
                      "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-input-border-focus",
                      isCurrent ? "bg-sidebar-active-bg" : "hover:bg-hover-bg",
                    )}
                  >
                    <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm bg-bg-secondary text-text-secondary">
                      <Icon size={18} aria-hidden />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="flex items-center gap-1.5 truncate text-body font-medium text-text">
                        {t(section.title)}
                        {isCurrent ? (
                          <span className="rounded-[3px] bg-accent px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-accent-text">
                            {t("Current")}
                          </span>
                        ) : null}
                      </span>
                      {section.description ? (
                        <span className="truncate text-small text-text-tertiary">
                          {t(section.description)}
                        </span>
                      ) : null}
                    </span>
                    <span aria-hidden className="shrink-0 text-small text-text-tertiary">
                      {section.count}
                    </span>
                    <CaretRight
                      size={12}
                      aria-hidden
                      className="shrink-0 text-text-tertiary rtl:rotate-180"
                    />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BackRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-nav-row=""
      className="inline-flex h-6 items-center gap-1 self-start rounded-sm ps-1 pe-2 text-small font-medium text-text-secondary outline-none hover:bg-hover-bg hover:text-text focus-visible:bg-hover-bg focus-visible:text-text"
    >
      <CaretLeft size={12} aria-hidden className="rtl:rotate-180" />
      {label}
    </button>
  );
}

interface SectionLayerProps {
  section: NavLayerSection;
  activeId?: string;
  onNavigate: () => void;
  onOpenSection: () => void;
}

/** Layer 1: every destination of one section, sub-grouped. */
function SectionLayer({ section, activeId, onNavigate, onOpenSection }: SectionLayerProps) {
  const { t } = useTranslation();
  const Icon = section.icon;
  const showHeadings = section.groups.length > 1 || section.groups.some((group) => group.title);

  return (
    <>
      <button
        type="button"
        onClick={onOpenSection}
        data-nav-row=""
        title={t("Open {{section}}", { section: t(section.title) })}
        className="mx-1 mb-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-sm px-2 py-1.5 text-start outline-none hover:bg-hover-bg focus-visible:bg-hover-bg"
      >
        <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm bg-bg-secondary text-text-secondary">
          <Icon size={18} aria-hidden />
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-body font-medium text-text">{t(section.title)}</span>
          {section.description ? (
            <span className="truncate text-small text-text-tertiary">{t(section.description)}</span>
          ) : null}
        </span>
      </button>
      {section.groups.map((group) => (
        <div key={group.id}>
          {showHeadings && group.title ? <GroupHeading>{t(group.title)}</GroupHeading> : null}
          <div className="flex flex-col gap-0.5 px-1">
            {group.items.map((destination) => (
              <NavRow
                key={destination.id}
                destination={destination}
                active={destination.id === activeId}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
